import { useState, useEffect, useMemo, useRef } from 'react'
import { useNavigate } from 'react-router-dom'

import { Loading } from './../../../Common/Loading'
import { Search } from './../../../Common/Tables/Search'
import { InstructorBase } from './../../../Layouts/Instructor/V2/InstructorBase'
import { Confirm } from './../../../Common/Modals/Confirm'
import { Details } from './../../../Common/Modals/Details'

import { useFloatMessage, MESSAGE_TYPE } from './../../../Providers/FloatMessage'
import { LogErrors } from './../../../../utilities/LogErrors'
import { ValidateSession } from './../../../../utilities/BL/Sessions'
import { HELP_TYPE } from './../../../../utilities/EL/HelpType'
import { WEBPAGES } from './../../../../utilities/EL/WebPages'

// --- Importaciones de la Capa de Negocios ---
// Reutilizamos GetInstructorsList del módulo de Results que creamos anteriormente
import { GetInstructorsList } from './../../../../utilities/BL/Leader/Results'
import { MoveGroupsBetweenInstructors } from './../../../../utilities/BL/Leader/ChangeGroup'
import { ROLES } from '../../../../utilities/EL/Roles'


const actions = {
    MOVE: 'MOVE'
}


const ChangeGroup = () => {
    const navigate = useNavigate()
    const { showMessage } = useFloatMessage()

    // ---------------------------------------------------------------------------
    // Estados Generales
    // ---------------------------------------------------------------------------
    const [isLoading, setIsLoading] = useState(true)
    const [isWorking, setIsWorking] = useState(false)
    const [errorMessage, setErrorMessage] = useState(null)

    // ---------------------------------------------------------------------------
    // Selectores de Instructores
    // ---------------------------------------------------------------------------
    const [instructorsList, setInstructorsList] = useState([])
    const [selectedOriginInstructorId, setSelectedOriginInstructorId] = useState(0)
    const [selectedDestinationInstructorId, setSelectedDestinationInstructorId] = useState(0)

    // ---------------------------------------------------------------------------
    // Tabla de Grupos y Buscador
    // ---------------------------------------------------------------------------
    const [groupsRows, setGroupsRows] = useState([]) 
    const [searchText, setSearchText] = useState('')
    const [selectedGroupIds, setSelectedGroupIds] = useState([]) // Checkboxes

    const groupsRowsRef = useRef([])
    useEffect(() => {
        groupsRowsRef.current = groupsRows
    }, [groupsRows])

    // ---------------------------------------------------------------------------
    // Modales (Confirmación y Resumen)
    // ---------------------------------------------------------------------------
    const [showConfirmationModal, setShowConfirmationModal] = useState(false)
    const [groupConfirmDetails, setGroupConfirmDetails] = useState(null) // Para movimiento individual
    const [actionTypeOfModal, setActionTypeOfModal] = useState(null)

    const [massiveSummary, setMassiveSummary] = useState(null)
    const [showMassiveSummaryModal, setShowMassiveSummaryModal] = useState(false)

    // ---------------------------------------------------------------------------
    // Lógica de Filtrado Local (Buscador)
    // ---------------------------------------------------------------------------
    const processedRows = useMemo(() => {
        const text = (searchText ?? '').toLowerCase()
        if (!text) return groupsRows

        return groupsRows.filter(group => {
            const fields = [group.groupName, group.groupAccessCode]
            return fields.some(v => String(v ?? '').toLowerCase().includes(text))
        })
    }, [groupsRows, searchText])

    const isGroupSelected = (idGroup) => selectedGroupIds.includes(idGroup)

    const toggleGroupSelect = (idGroup) => {
        setSelectedGroupIds(prev =>
            prev.includes(idGroup) ? prev.filter(id => id !== idGroup) : [...prev, idGroup]
        )
    }

    const clearSelections = () => setSelectedGroupIds([])

    const getSelectedGroupsByIds = (ids) => {
        return ids
            .map(id => groupsRowsRef.current.find(g => g.idGroup === id))
            .filter(Boolean)
    }

    // ---------------------------------------------------------------------------
    // Carga Inicial de Datos
    // ---------------------------------------------------------------------------
    useEffect(() => {
        async function ValidateAndInit() {
            try {
                const tmpSession = await ValidateSession()
                if (tmpSession?.success && tmpSession.data?.user?.userStatus === 2) {
                    await LoadInstructors()
                } else {
                    navigate('/confirm') // Ajustar según el ruteo de seguridad del líder
                }
            } catch (error) {
                await LogErrors('ValidateAndInit - Leaders ChangeGroup', error)
                navigate(`/?logout=${process.env.REACT_APP_LOGOUT_PARAMETER}`)
            }
        }
        ValidateAndInit()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const LoadInstructors = async () => {
        try {
            setIsLoading(true)
            setIsWorking(true)
            setErrorMessage(null)

            const response = await GetInstructorsList()
            if (response.success) {
                setInstructorsList(response.data || [])
                
                // Si la función se llama después de un movimiento exitoso, 
                // re-poblamos la tabla del instructor origen actual para que se refresque visualmente
                if (selectedOriginInstructorId !== 0) {
                    const originInst = response.data.find(i => i.idInstructor === selectedOriginInstructorId)
                    setGroupsRows(originInst?.groups || [])
                }
            } else {
                setErrorMessage(response.message || 'No fue posible cargar el catálogo de instructores.')
            }
        } catch (error) {
            await LogErrors('LoadInstructors - Leaders ChangeGroup', error)
            setErrorMessage('Ocurrió un error al cargar la información.')
        } finally {
            setIsWorking(false)
            setIsLoading(false)
        }
    }

    // ---------------------------------------------------------------------------
    // Manejo de Selectores
    // ---------------------------------------------------------------------------
    const handleSelectOriginInstructor = (instructorId) => {
        const id = parseInt(instructorId, 10) || 0
        setSelectedOriginInstructorId(id)

        // Limpiamos selecciones y búsqueda
        clearSelections()
        setSearchText('')
        
        // Si elige el mismo que el destino, reseteamos el destino por seguridad
        if (id === selectedDestinationInstructorId) {
            setSelectedDestinationInstructorId(0)
        }

        // Poblamos la tabla inmediatamente extrayendo los grupos del instructor seleccionado
        if (id !== 0) {
            const selectedInstructor = instructorsList.find(inst => inst.idInstructor === id)
            setGroupsRows(selectedInstructor?.groups || [])
        } else {
            setGroupsRows([])
        }
    }

    const handleSelectDestinationInstructor = (instructorId) => {
        const id = parseInt(instructorId, 10) || 0
        setSelectedDestinationInstructorId(id)
    }

    // Filtramos la lista para el combo de Destino (No puede enviarse a sí mismo)
    const destinationInstructorsEligible = useMemo(() => {
        return instructorsList.filter(inst => inst.idInstructor !== selectedOriginInstructorId)
    }, [instructorsList, selectedOriginInstructorId])

    // ---------------------------------------------------------------------------
    // Lógica de Movimiento de Grupos (Individual y Masivo)
    // ---------------------------------------------------------------------------

    const ShowConfirmMoveModal = (groupRow) => {
        if (!selectedDestinationInstructorId || selectedDestinationInstructorId === 0) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Selecciona primero el Instructor de destino.' })
            return
        }

        setActionTypeOfModal(actions.MOVE)
        setGroupConfirmDetails(groupRow)
        setShowConfirmationModal(true)
    }

    const ExecuteTransfer = async (groupsArray) => {
        setIsWorking(true)

        try {
            const originInstructor = instructorsList.find(i => i.idInstructor === selectedOriginInstructorId)
            const destInstructor = instructorsList.find(i => i.idInstructor === selectedDestinationInstructorId)

            if (!originInstructor || !destInstructor) {
                showMessage({ type: MESSAGE_TYPE.BAD, text: 'Información de instructores inválida.' })
                return false
            }

            // Extraemos los IDs de los grupos
            const idsGroupsToMove = groupsArray.map(g => g.idGroup)

            // Llamada al BL pasando los idCenterUser como pide tu API
            const response = await MoveGroupsBetweenInstructors(
                originInstructor.idCenterUser,
                destInstructor.idCenterUser,
                idsGroupsToMove
            )

            if (response.success) {
                showMessage({ 
                    type: MESSAGE_TYPE.SUCCESS, 
                    text: `Se transfirieron ${groupsArray.length} grupo(s) con éxito de ${originInstructor.instructorName} a ${destInstructor.instructorName}.` 
                })
                
                // Limpiar check boxes y recargar datos de instructores
                clearSelections()
                await LoadInstructors() 
                return true
            } else {
                showMessage({ type: MESSAGE_TYPE.BAD, text: response.message || 'Error al realizar la transferencia.' })
                return false
            }
        } catch (error) {
            await LogErrors('ExecuteTransfer - Leaders ChangeGroup', error)
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error interno en la ejecución del movimiento.' })
            return false
        } finally {
            setIsWorking(false)
        }
    }

    const MoveSingleGroup = async () => {
        if (!groupConfirmDetails) return
        setShowConfirmationModal(false)
        await ExecuteTransfer([groupConfirmDetails])
    }

    const HandleMassiveMoveGroups = async () => {
        if (!selectedDestinationInstructorId || selectedDestinationInstructorId === 0) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Selecciona el Instructor de Destino.' })
            return
        }

        if (selectedGroupIds.length === 0) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Selecciona al menos un grupo para mover.' })
            return
        }

        const groupsToMove = getSelectedGroupsByIds(selectedGroupIds)
        
        // Ejecutamos transferencia
        const isSuccess = await ExecuteTransfer(groupsToMove)

        // Si fue un 200 OK general, armamos el resumen
        if (isSuccess) {
            setMassiveSummary({
                successful: groupsToMove,
                wrongs: [],
                omitteds: []
            })
            setShowMassiveSummaryModal(true)
        }
    }


    // ---------------------------------------------------------------------------
    // RENDER
    // ---------------------------------------------------------------------------
    return isLoading ? (
        <Loading />
    ) : (
        <InstructorBase urlToGo={'/dashboard'} idHelp={HELP_TYPE.LEADER.CHANGE_GROUP} page={WEBPAGES.LEADER.CHANGE_GROUP} role={ROLES.LEADER}>
            {isWorking && <Loading />}

            <section className='container Instructor__Reports'>
                <p className='Instructor__Title Instructor__Title--Center Instructor__SpaceUp'>
                    Mover grupo
                </p>

                {/* ======================= ZONA DE FILTROS ======================= */}
                <div className='row justify-content-center Instructor__SpaceUp'>
                    
                    {/* INSTRUCTOR ORIGEN */}
                    <div className='col-12 col-md-5'>
                        <p className='text-center Instructor__Text--Bold'>Origen</p>
                        <select
                            className='Instructor__ChangeGroup__Reports__Filters__List'
                            value={selectedOriginInstructorId}
                            onChange={(e) => handleSelectOriginInstructor(e.target.value)}
                            disabled={isWorking || instructorsList.length === 0}
                        >
                            <option value={0}>Selecciona un instructor</option>
                            {instructorsList.map(inst => (
                                <option key={`from-${inst.idInstructor}`} value={inst.idInstructor}>
                                    {inst.instructorName}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className='col-12 col-md-2 d-flex align-items-center justify-content-center my-3 my-md-0'>
                        <i className={`bi bi-chevron-double-right fs-1 d-none d-md-block ${selectedDestinationInstructorId === 0 ? 'text-muted' : 'text-success'}`}></i>
                        <i className={`bi bi-chevron-double-down fs-1 d-block d-md-none ${selectedDestinationInstructorId === 0 ? 'text-muted' : 'text-success'}`}></i>
                    </div>

                    {/* INSTRUCTOR DESTINO */}
                    <div className='col-12 col-md-5'>
                        <p className='text-center Instructor__Text--Bold'>Destino</p>
                        <select
                            className='Instructor__ChangeGroup__Reports__Filters__List'
                            value={selectedDestinationInstructorId}
                            onChange={(e) => handleSelectDestinationInstructor(e.target.value)}
                            disabled={isWorking || destinationInstructorsEligible.length === 0 || selectedOriginInstructorId === 0}
                        >
                            <option value={0}>Selecciona un instructor</option>
                            {destinationInstructorsEligible.map(inst => (
                                <option key={`to-${inst.idInstructor}`} value={inst.idInstructor}>
                                    {inst.instructorName}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                {errorMessage && errorMessage !== '' && (
                    <p className='Instructor__Message--Error Instructor__SpaceUp text-center'>{errorMessage}</p>
                )}


                {/* ======================= TABLA DE GRUPOS ======================= */}
                {selectedOriginInstructorId !== 0 && (
                    <>
                        <div className='Instructor__SpaceUp' style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Search onChange={setSearchText} />
                        </div>

                        <section className='Tables__Container Instructor__Progress__ContainerTable Tables--Shadow Tables--Background Instructor__SpaceUp'>
                            
                            {/* Panel de Acción Masiva */}
                            {selectedGroupIds.length > 0 && (
                                <div className='container py-3 border-bottom'>
                                    <div className='d-flex flex-column flex-md-row justify-content-between align-items-center'>
                                        <div className='d-flex align-items-center gap-3 mb-3 mb-md-0'>
                                            <span className='badge bg-primary fs-6'>{selectedGroupIds.length} seleccionados</span>
                                            <button type='button' className='btn btn-sm btn-outline-secondary' onClick={clearSelections}>Limpiar</button>
                                        </div>
                                        <button
                                            className='Btn__Ztudia--Primary'
                                            onClick={HandleMassiveMoveGroups}
                                            disabled={isWorking}
                                        >
                                            <i className="bi bi-arrow-left-right"></i> Mover grupos seleccionados
                                        </button>
                                    </div>
                                </div>
                            )}

                            <table className='Tables__Table Instructor__Progress__ContainerTable__Table'>
                                <thead>
                                    <tr className='Tables__Table__Row--Header'>
                                        <td className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__InputColumn'>
                                            <input
                                                type='checkbox'
                                                className='Tables__Table__InputColumn__Input'
                                                onChange={(e) => {
                                                    const newIds = e.target.checked ? processedRows.map(row => row.idGroup) : []
                                                    setSelectedGroupIds(newIds)
                                                }}
                                                checked={processedRows.length > 0 && processedRows.every(row => selectedGroupIds.includes(row.idGroup))}
                                            />
                                        </td>
                                        <td className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__TextColumn'>
                                            Nombre del Grupo
                                        </td>
                                        <td className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__TextColumn'>
                                            Código de Acceso
                                        </td>
                                        <td className='Tables__Table__Row__Cell Tables__Table__Header Tables__Table__ActionButtonsColumn'></td>
                                    </tr>
                                </thead>
                                <tbody>
                                    {processedRows?.length ? (
                                        processedRows.map((g, idx) => (
                                            <tr key={g.idGroup ?? idx} className='Tables__Table__Row'>
                                                <td className='Tables__Table__Row__Cell Tables__Table__InputColumn'>
                                                    <input
                                                        type='checkbox'
                                                        className='Tables__Table__InputColumn__Input'
                                                        checked={isGroupSelected(g.idGroup)}
                                                        onChange={() => toggleGroupSelect(g.idGroup)}
                                                    />
                                                </td>
                                                <td className='Tables__Table__Row__Cell Tables__Table__TextColumn Instructor__Text--Bold'>
                                                    {g.groupName}
                                                </td>
                                                <td className='Tables__Table__Row__Cell Tables__Table__TextColumn'>
                                                    {g.groupAccessCode}
                                                </td>
                                                <td className='Tables__Table__Row__Cell Tables__Table__ActionButtonsColumn'>
                                                    <button
                                                        type='button'
                                                        className='Btn__Ztudia--PrimaryOutline'
                                                        onClick={() => ShowConfirmMoveModal(g)}
                                                        disabled={isWorking}
                                                    >
                                                        Mover grupo
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr className='Tables__Table__Row'>
                                            <td className='Tables__Table__Row__Cell text-center py-4' colSpan={4}>
                                                {instructorsList.find(i => i.idInstructor === selectedOriginInstructorId)?.groups?.length === 0 
                                                    ? 'Este instructor no tiene grupos asignados para mover.' 
                                                    : 'No hay coincidencias en tu búsqueda.'}
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </section>
                    </>
                )}


                {/* ======================= MODALES ======================= */}
                
                {/* Modal: Confirmación Individual */}
                {showConfirmationModal && groupConfirmDetails && actionTypeOfModal === actions.MOVE && (
                    <Confirm
                        message={(() => {
                            const originInst = instructorsList.find(i => i.idInstructor === selectedOriginInstructorId)
                            const destInst = instructorsList.find(i => i.idInstructor === selectedDestinationInstructorId)
                            return `¿Estás seguro de mover el grupo "${groupConfirmDetails.groupName}" del instructor ${originInst?.instructorName} hacia el instructor ${destInst?.instructorName}?`
                        })()}
                        actionToYesButton={MoveSingleGroup}
                        textYesButton={'Sí, mover grupo'}
                        textNoButton={'Cancelar'}
                        setShowModal={setShowConfirmationModal}
                        setConfirmData={setGroupConfirmDetails}
                    />
                )}

                {/* Modal: Resumen Masivo */}
                {showMassiveSummaryModal && massiveSummary && (
                    <Details stepButtons={false} showCloseModalButtonInContent={true} extraClassName={'Modal__Layout--Summary'} setShowModal={setShowMassiveSummaryModal} setDetailsData={setMassiveSummary}>
                        <div className='Modal__Layout--Summary__Container'>
                            <p className='Modal__Layout--Summary__Title'>
                                Resumen de grupos movidos
                            </p>

                            {massiveSummary.successful.length > 0 && (
                                <>
                                    <p className='Modal__Layout--Summary__Category Instructor__Text--Success'>
                                        <i className='bi bi-check-circle'></i> Grupos movidos exitosamente:
                                    </p>
                                    <div className='Modal__Layout--Summary__TextContainer'>
                                        {massiveSummary.successful.map((success, index) => (
                                            <p key={index} className='Modal__Layout--Summary__TextContainer__Text'>
                                                {success.groupName} (Código: {success.groupAccessCode})
                                            </p>
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>
                    </Details>
                )}

            </section>
        </InstructorBase>
    )
}

export { ChangeGroup };